import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { articleKeys, type Article } from './articles';
import type { DecimalString } from '@efacture/web-billing/lib/decimal';

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────

export type StockBatchStatus = 'ACTIVE' | 'EXPIRED' | 'DEPLETED';

export type StockMovementType = 'IN' | 'OUT' | 'ADJUST' | 'TRANSFER' | 'LOSS' | 'RETURN';

export interface StockLocation {
  id: string;
  name: string;
  code: string;
  isDefault: boolean;
  /** L'établissement du dépôt ; null compte pour l'établissement par défaut. */
  establishmentId: string | null;
}

export interface StockBatch {
  id: string;
  articleId: string;
  article?: Article;
  batchNumber: string;
  expirationDate: string | null;
  initialQty: DecimalString;
  currentQty: DecimalString;
  unitCost: DecimalString | null;
  status: StockBatchStatus;
  receivedAt: string;
}

export interface StockMovement {
  id: string;
  articleId: string;
  article?: Article;
  batchId: string | null;
  type: StockMovementType;
  quantity: DecimalString;
  reason: string | null;
  reference: string | null;
  createdAt: string;
  createdBy: string;
  /** The author, joined by GET /stock/movements (StockService.listMovements). */
  user?: { id: string; firstName: string; lastName: string } | null;
  /** Null on history written before movements recorded a depot. */
  location?: { id: string; name: string; code: string } | null;
}

export interface StockOverview {
  totalArticles: number;
  stockManagedArticles: number;
  lowStockCount: number;
  expiringCount: number;
  /** Null when a local node holds a site: the centre's total does not cover its stock. */
  totalValue: number | null;
  unvaluedArticles: number;
}

export interface StockBatchesParams {
  articleId?: string;
  status?: StockBatchStatus;
  page?: number;
  limit?: number;
}

export interface StockMovementsParams {
  articleId?: string | undefined;
  type?: StockMovementType | undefined;
  startDate?: string | undefined;
  endDate?: string | undefined;
  locationId?: string | undefined;
  page?: number | undefined;
  limit?: number | undefined;
  /** The exact total a previous page already returned for this filter set. */
  knownTotal?: number | undefined;
}

export interface StockMovementsResponse {
  movements: StockMovement[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasMore: boolean;
  };
}

export interface ReceiveStockData {
  articleId: string;
  batchNumber: string;
  quantity: number;
  unitCost?: number | undefined;
  expirationDate?: string | undefined;
  /** Where the goods land; the server takes the default when absent. */
  locationId?: string | undefined;
  /**
   * Opening stock of a migration, carried by the imported à-nouveau on the
   * 31 accounts: never posted, a unit cost is required (it sets the CUMP).
   */
  opening?: boolean | undefined;
}

export interface AdjustStockData {
  articleId: string;
  batchId?: string;
  /** Signed: positive adds stock, negative removes it. */
  quantity: number;
  type: 'ADJUST' | 'LOSS' | 'RETURN';
  reason: string;
  /** Omitted: the tenant's default depot. */
  locationId?: string;
}

/** What a withdrawal takes, or would take, from one lot. */
export interface DrawnLot {
  batchNumber: string;
  quantity: DecimalString;
}

export interface AdjustStockResult {
  movement: StockMovement;
  article: Article;
  /** The lots the withdrawal took; absent from a response replayed from an older API. */
  drawn?: DrawnLot[];
  /** False on an install where a physical inventory is refused; absent from an older API (read as true). */
  inventoryAvailable?: boolean;
}

/** A lot a withdrawal can name, in the order an automatic draw takes it. */
export interface DepotLot {
  id: string;
  batchNumber: string;
  currentQty: DecimalString;
  expirationDate: string | null;
  pastDate: boolean;
}

export interface DepotLotsRead {
  /** The depot read: the one named, else the one an unnamed adjustment lands in. */
  locationId: string | null;
  lots: DepotLot[];
  /** What the quantity asked would draw if the withdrawal names no lot. */
  drawn: DrawnLot[];
}

export interface DepotLotsParams {
  locationId?: string | undefined;
  quantity?: number | undefined;
}

export interface LowStockArticle {
  id: string;
  code: string;
  name: string;
  stockQty: DecimalString;
  minStockAlert: DecimalString;
  unit: string;
}

/** One row of GET /stock/expiring: the batch with its article joined. */
export interface ExpiringBatch {
  id: string;
  articleId: string;
  article: { id: string; code: string; name: string };
  batchNumber: string;
  currentQty: DecimalString;
  expirationDate: string;
  /** Whole days from now to `expirationDate`, rounded up by the API. */
  daysUntilExpiration: number;
}

// ─────────────────────────────────────────────────────────────
// Query Keys
// ─────────────────────────────────────────────────────────────

export const stockKeys = {
  all: ['stock'] as const,
  locations: () => [...stockKeys.all, 'locations'] as const,
  heldLocations: () => [...stockKeys.all, 'locations', 'held'] as const,
  sessionDefaultLocation: () => [...stockKeys.all, 'locations', 'session-default'] as const,
  overview: () => [...stockKeys.all, 'overview'] as const,
  batches: () => [...stockKeys.all, 'batches'] as const,
  batchesList: (params: StockBatchesParams) => [...stockKeys.batches(), params] as const,
  lowStock: () => [...stockKeys.all, 'low'] as const,
  expiring: (days?: number) => [...stockKeys.all, 'expiring', days] as const,
  movements: () => [...stockKeys.all, 'movements'] as const,
  movementsList: (params: StockMovementsParams) => [...stockKeys.movements(), params] as const,
  holdings: () => [...stockKeys.all, 'holdings'] as const,
  onHand: (articleId: string) => [...stockKeys.all, 'on-hand', articleId] as const,
  depotLots: (articleId: string, params: DepotLotsParams) => [...stockKeys.all, 'depot-lots', articleId, params] as const,
};

// ─────────────────────────────────────────────────────────────
// API Functions
// ─────────────────────────────────────────────────────────────

export interface TransferStockData {
  articleId: string;
  fromLocationId: string;
  toLocationId: string;
  quantity: number;
  reason?: string;
}

/** Ce qu'un dépôt détient, pour l'écran des dépôts. */
export interface LocationHolding {
  locationId: string;
  articles: number;
  /** Converted server-side via Number(_sum.qty), never a raw Decimal. */
  quantity: number;
}

/** La quantité d'UN article dans chaque dépôt. */
export interface ArticleOnHandRow {
  locationId: string;
  locationCode: string;
  locationName: string;
  /** Converted server-side via Number(row.qty), never a raw Decimal. */
  quantity: number;
}

export interface StockLocationInput {
  code: string;
  name: string;
  isDefault?: boolean;
  /** Requis quand le dossier compte plusieurs établissements. */
  establishmentId?: string;
}

/** Le dépôt présélectionné à l'ouverture de caisse. */
export interface SessionDefaultLocation {
  locationId: string | null;
}

async function fetchStockLocations(): Promise<StockLocation[]> {
  return apiClient.get<StockLocation[]>('/stock/locations');
}

async function fetchHeldStockLocations(): Promise<StockLocation[]> {
  return apiClient.get<StockLocation[]>('/stock/locations?held=1');
}

async function fetchLocationHoldings(): Promise<LocationHolding[]> {
  const res = await apiClient.get<{ holdings: LocationHolding[] }>(
    '/stock/locations/holdings'
  );
  return res.holdings;
}

async function fetchArticleOnHand(articleId: string): Promise<ArticleOnHandRow[]> {
  const res = await apiClient.get<{ onHand: ArticleOnHandRow[] }>(
    `/stock/articles/${articleId}/on-hand`
  );
  return res.onHand;
}

async function transferStock(data: TransferStockData): Promise<unknown> {
  return apiClient.post<unknown>('/stock/transfer', data);
}

async function createStockLocation(data: StockLocationInput): Promise<StockLocation> {
  return apiClient.post<StockLocation>('/stock/locations', data);
}

async function updateStockLocation(
  id: string,
  data: Partial<StockLocationInput>
): Promise<StockLocation> {
  return apiClient.patch<StockLocation>(`/stock/locations/${id}`, data);
}

async function moveStockLocation(id: string, establishmentId: string): Promise<StockLocation> {
  return apiClient.post<StockLocation>(`/stock/locations/${id}/establishment`, { establishmentId });
}

async function fetchSessionDefaultLocation(): Promise<SessionDefaultLocation> {
  return apiClient.get<SessionDefaultLocation>('/stock/locations/session-default');
}

async function fetchStockOverview(): Promise<StockOverview> {
  return apiClient.get<StockOverview>('/stock/overview');
}

async function fetchStockBatches(params: StockBatchesParams): Promise<StockBatch[]> {
  const queryParams: Record<string, string | number | boolean | undefined> = {
    articleId: params.articleId,
    status: params.status,
    page: params.page,
    limit: params.limit,
  };
  return apiClient.get<StockBatch[]>('/stock/batches', queryParams);
}

async function fetchLowStockArticles(): Promise<LowStockArticle[]> {
  return apiClient.get<LowStockArticle[]>('/stock/low');
}

async function fetchExpiringBatches(days?: number): Promise<ExpiringBatch[]> {
  const queryParams: Record<string, string | number | boolean | undefined> = {
    days: days ?? 30,
  };
  return apiClient.get<ExpiringBatch[]>('/stock/expiring', queryParams);
}

async function fetchStockMovements(params: StockMovementsParams): Promise<StockMovementsResponse> {
  const queryParams: Record<string, string | number | boolean | undefined> = {
    articleId: params.articleId,
    type: params.type,
    startDate: params.startDate,
    endDate: params.endDate,
    locationId: params.locationId,
    page: params.page,
    limit: params.limit,
    knownTotal: params.knownTotal,
  };
  return apiClient.get<StockMovementsResponse>('/stock/movements', queryParams);
}

async function receiveStock(data: ReceiveStockData, idempotencyKey: string): Promise<StockBatch> {
  return apiClient.post<StockBatch>('/stock/receive', data, { idempotencyKey });
}

async function adjustStock(data: AdjustStockData, idempotencyKey: string): Promise<AdjustStockResult> {
  return apiClient.post<AdjustStockResult>('/stock/adjust', data, { idempotencyKey });
}

// ─────────────────────────────────────────────────────────────
// Queries
// ─────────────────────────────────────────────────────────────

/**
 * Hook to fetch stock locations (points de vente)
 * GET /api/stock/locations
 */
export function useStockLocations(options?: { enabled?: boolean; heldOnly?: boolean }) {
  const heldOnly = options?.heldOnly ?? false;
  return useQuery({
    queryKey: heldOnly ? stockKeys.heldLocations() : stockKeys.locations(),
    queryFn: heldOnly ? fetchHeldStockLocations : fetchStockLocations,
    staleTime: 1000 * 60 * 5, // 5 minutes - locations rarely change
    enabled: options?.enabled ?? true,
  });
}

/**
 * Le dépôt par défaut de l'établissement de l'utilisateur, présélectionné à
 * l'ouverture de caisse.
 * GET /api/stock/locations/session-default
 */
export function useSessionDefaultLocation(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: stockKeys.sessionDefaultLocation(),
    queryFn: fetchSessionDefaultLocation,
    staleTime: 1000 * 60 * 5,
    enabled: options?.enabled ?? true,
  });
}

/**
 * Ce que chaque dépôt détient.
 * GET /api/stock/locations/holdings
 *
 * Requête SÉPARÉE de `useStockLocations` : celle-là remplit les listes
 * déroulantes partout, jusqu'au dialogue d'ouverture de caisse, et n'a pas à
 * transporter un agrégat pour cela.
 */
export function useLocationHoldings(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: stockKeys.holdings(),
    queryFn: fetchLocationHoldings,
    enabled: options?.enabled ?? true,
  });
}

/**
 * La quantité d'un article dans chaque dépôt — le chiffre que le refus de
 * transfert mesurera.
 * GET /api/stock/articles/:articleId/on-hand
 */
export function useArticleOnHand(articleId: string | undefined) {
  return useQuery({
    queryKey: stockKeys.onHand(articleId ?? ''),
    queryFn: () => fetchArticleOnHand(articleId!),
    enabled: Boolean(articleId),
  });
}

/**
 * The lots a withdrawal can name at a depot, and what a quantity would draw.
 * Disabled with no article.
 * GET /api/stock/articles/:articleId/lots
 */
export function useDepotLots(articleId: string | undefined, params: DepotLotsParams) {
  return useQuery({
    queryKey: stockKeys.depotLots(articleId ?? '', params),
    queryFn: () => apiClient.get<DepotLotsRead>(`/stock/articles/${articleId}/lots`, { ...params }),
    enabled: Boolean(articleId),
  });
}

/**
 * Déplacer du stock entre deux dépôts.
 * POST /api/stock/transfer
 */
export function useTransferStock() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: TransferStockData) => transferStock(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: stockKeys.all });
    },
  });
}

/**
 * Créer un dépôt.
 * POST /api/stock/locations
 */
export function useCreateStockLocation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: StockLocationInput) => createStockLocation(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: stockKeys.all });
    },
  });
}

/**
 * Renommer un dépôt, ou le désigner par défaut.
 * PATCH /api/stock/locations/:id
 */
export function useUpdateStockLocation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: Partial<StockLocationInput> & { id: string }) =>
      updateStockLocation(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: stockKeys.all });
    },
  });
}

/**
 * Rattacher un dépôt à un autre établissement.
 * POST /api/stock/locations/:id/establishment
 */
export function useMoveStockLocation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, establishmentId }: { id: string; establishmentId: string }) =>
      moveStockLocation(id, establishmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: stockKeys.all });
    },
  });
}

/**
 * Hook to fetch stock overview statistics
 * GET /api/stock/overview
 */
export function useStockOverview(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: stockKeys.overview(),
    queryFn: fetchStockOverview,
    staleTime: 1000 * 60 * 2, // 2 minutes
    enabled: options?.enabled ?? true,
  });
}

/**
 * Hook to fetch stock batches
 * GET /api/stock/batches
 */
export function useStockBatches(params: StockBatchesParams = {}, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: stockKeys.batchesList(params),
    queryFn: () => fetchStockBatches(params),
    enabled: options?.enabled ?? true,
  });
}

/**
 * Hook to fetch low stock articles
 * GET /api/stock/low
 */
export function useLowStockArticles(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: stockKeys.lowStock(),
    queryFn: fetchLowStockArticles,
    enabled: options?.enabled ?? true,
  });
}

/**
 * Hook to fetch expiring batches
 * GET /api/stock/expiring
 */
export function useExpiringBatches(days?: number, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: stockKeys.expiring(days),
    queryFn: () => fetchExpiringBatches(days),
    enabled: options?.enabled ?? true,
  });
}

/**
 * Hook to fetch stock movements
 * GET /api/stock/movements
 */
export function useStockMovements(params: StockMovementsParams = {}, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: stockKeys.movementsList(params),
    queryFn: () => fetchStockMovements(params),
    enabled: options?.enabled ?? true,
    select: (response) => ({
      movements: response.movements,
      pagination: response.pagination,
    }),
  });
}

// ─────────────────────────────────────────────────────────────
// Mutations
// ─────────────────────────────────────────────────────────────

/**
 * Hook to receive new stock (create batch)
 * POST /api/stock/receive — `idempotencyKey` is the form's key, shared by
 * every submit of one receipt.
 */
export function useReceiveStock() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ data, idempotencyKey }: { data: ReceiveStockData; idempotencyKey: string }) =>
      receiveStock(data, idempotencyKey),
    onSuccess: () => {
      // Invalidate stock-related queries
      queryClient.invalidateQueries({ queryKey: stockKeys.all });
    },
  });
}

/**
 * Hook to adjust stock
 * POST /api/stock/adjust — `idempotencyKey` is the form's key, shared by
 * every submit of one adjustment.
 */
export function useAdjustStock() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ data, idempotencyKey }: { data: AdjustStockData; idempotencyKey: string }) =>
      adjustStock(data, idempotencyKey),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: stockKeys.all });
      // The article's stockQty moved too.
      queryClient.invalidateQueries({ queryKey: articleKeys.all });
    },
  });
}

// ─────────────────────────────────────────────────────────────
// Utility Exports
// ─────────────────────────────────────────────────────────────

export {
  fetchStockLocations,
  fetchStockOverview,
  fetchStockBatches,
  fetchLowStockArticles,
  fetchExpiringBatches,
  fetchStockMovements,
  receiveStock,
  adjustStock,
};
